const MANAGED_BUCKETS = new Set([
  "batch-materials",
  "course-content",
  "course-resources",
  "course-thumbnails",
  "course-videos",
  "lesson-documents",
  "scorm-packages",
]);

const referenceKey = ({ bucket, path, folder }) =>
  `${bucket}:${folder ? "folder" : "file"}:${path}`;

const cleanPath = (value) => {
  try {
    return decodeURIComponent(String(value || "").split("?")[0]).replace(
      /^\/+|\/+$/g,
      "",
    );
  } catch {
    return String(value || "").split("?")[0].replace(/^\/+|\/+$/g, "");
  }
};

export const parseManagedStorageReference = (value) => {
  if (typeof value !== "string" || !value.trim()) return null;
  const raw = value.trim();

  const storageMatch = raw.match(
    /\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)$/i,
  );
  if (storageMatch) {
    const bucket = storageMatch[1];
    if (!MANAGED_BUCKETS.has(bucket)) return null;
    const path = cleanPath(storageMatch[2]);
    return {
      bucket,
      path,
      folder: path.startsWith("scorm-extracted/") ||
        path.includes("/scorm-extracted/"),
    };
  }

  const path = cleanPath(raw);
  if (path.startsWith("scorm-extracted/") || path.includes("/scorm-extracted/")) {
    return { bucket: "course-content", path, folder: true };
  }
  if (path.startsWith("course-materials/")) {
    return { bucket: "batch-materials", path, folder: false };
  }

  return null;
};

const collectStrings = (value, output) => {
  if (typeof value === "string") {
    output.push(value);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectStrings(item, output));
    return;
  }
  if (value && typeof value === "object") {
    Object.values(value).forEach((item) => collectStrings(item, output));
  }
};

export const collectLessonStorageReferences = (lesson = {}) => {
  const values = [];
  collectStrings(lesson, values);
  const references = new Map();
  values.forEach((value) => {
    const reference = parseManagedStorageReference(value);
    if (reference) references.set(referenceKey(reference), reference);
  });
  return [...references.values()];
};

export const diffStorageReferences = (current, baseline) => {
  const previousKeys = new Set(
    collectLessonStorageReferences(baseline).map(referenceKey),
  );
  return collectLessonStorageReferences(current).filter(
    (reference) => !previousKeys.has(referenceKey(reference)),
  );
};

export const collectStorageFolderFiles = async (
  folderPath,
  listPage,
  pageSize = 1000,
) => {
  const files = [];
  const folders = [];
  let offset = 0;

  while (true) {
    const page = (await listPage(folderPath, { limit: pageSize, offset })) || [];
    for (const item of page) {
      const itemPath = `${folderPath}/${item.name}`;
      if (item.id != null || item.metadata != null) files.push(itemPath);
      else folders.push(itemPath);
    }
    if (page.length < pageSize) break;
    offset += page.length;
  }

  for (const childFolder of folders) {
    files.push(
      ...(await collectStorageFolderFiles(childFolder, listPage, pageSize)),
    );
  }
  return files;
};
